import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import {
  MessageSquare,
  Image as ImageIcon,
  Code2,
  ArrowRight,
  TrendingUp,
} from "lucide-react";

const TIER_LABEL = { free: "Free", starter: "Starter", pro: "Pro", business: "Business" };

export default function Dashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/user/dashboard").then((r) => setData(r.data));
  }, []);

  if (!data) {
    return (
      <div className="flex h-full items-center justify-center text-slate-400">
        {t("common.loading")}
      </div>
    );
  }

  const { quotas, recent_messages, recent_images, tier, next_plan, next_plan_price_eur } = data;

  return (
    <div className="px-8 py-10 lg:px-12" data-testid="dashboard-page">
      <header className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#9ACD32]">
            {t("dashboard.welcome")}
          </p>
          <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight text-white">
            {user?.name || user?.email?.split("@")[0]}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <span className={`tier-badge tier-${tier}`}>{TIER_LABEL[tier]}</span>
          {tier !== "business" && (
            <Button
              asChild
              size="sm"
              className="lime-glow rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]"
              data-testid="dashboard-upgrade-btn"
            >
              <Link to="/pricing">
                <TrendingUp className="mr-1.5 h-4 w-4" />
                {t("dashboard.upgrade_cta", { plan: TIER_LABEL[next_plan] || "Pro" })}
              </Link>
            </Button>
          )}
        </div>
      </header>

      <section className="mb-10">
        <h2 className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          {t("dashboard.usage_overview")}
        </h2>
        <div className="grid gap-4 md:grid-cols-3">
          <UsageCard
            icon={MessageSquare}
            label={t("dashboard.messages_used")}
            used={quotas.messages.used}
            limit={quotas.messages.limit}
            testId="usage-messages"
          />
          <UsageCard
            icon={ImageIcon}
            label={t("dashboard.images_used")}
            used={quotas.images.used}
            limit={quotas.images.limit}
            testId="usage-images"
          />
          <UsageCard
            icon={Code2}
            label={t("dashboard.code_used")}
            used={quotas.code_generations.used}
            limit={quotas.code_generations.limit}
            testId="usage-code"
          />
        </div>
      </section>

      <section className="mb-10">
        <h2 className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          {t("dashboard.quick_actions")}
        </h2>
        <div className="grid gap-4 md:grid-cols-3">
          <ActionCard to="/chat" icon={MessageSquare} label={t("dashboard.action_chat")} accent="lime" testId="action-chat" />
          <ActionCard to="/images" icon={ImageIcon} label={t("dashboard.action_image")} accent="navy" testId="action-image" />
          <ActionCard to="/code" icon={Code2} label={t("dashboard.action_code")} accent="navy" testId="action-code" />
        </div>
      </section>

      <section className="grid gap-6 md:grid-cols-2">
        <Card className="bg-[#0A0C1F] border-slate-800 p-6">
          <h3 className="mb-4 font-display text-lg font-bold text-white">
            {t("dashboard.recent_chats")}
          </h3>
          {recent_messages?.length ? (
            <ul className="space-y-3">
              {recent_messages.map((m, i) => (
                <li key={i} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <p className="line-clamp-2 text-sm text-slate-200">{m.content}</p>
                  <p className="mt-1 text-[11px] uppercase tracking-wider text-slate-500">
                    {m.model}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">{t("dashboard.no_activity")}</p>
          )}
        </Card>

        <Card className="bg-[#0A0C1F] border-slate-800 p-6">
          <h3 className="mb-4 font-display text-lg font-bold text-white">
            {t("dashboard.recent_images")}
          </h3>
          {recent_images?.length ? (
            <div className="grid grid-cols-2 gap-3">
              {recent_images.map((img, i) => (
                <div key={i} className="overflow-hidden rounded-lg border border-slate-800">
                  <img src={img.image_data_url} alt={img.prompt} loading="lazy" decoding="async" className="aspect-square w-full object-cover" />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">{t("dashboard.no_activity")}</p>
          )}
        </Card>
      </section>
    </div>
  );
}

function UsageCard({ icon: Icon, label, used, limit, testId }) {
  const pct = Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
  return (
    <Card className="border-slate-800 bg-[#0A0C1F] p-5" data-testid={testId}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Icon className="h-4 w-4 text-[#9ACD32]" />
          <span>{label}</span>
        </div>
        <span className="text-xs font-mono text-slate-500">{pct}%</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="font-display text-3xl font-extrabold text-white">{used}</span>
        <span className="text-sm text-slate-500">/ {limit}</span>
      </div>
      <Progress value={pct} className="mt-3 h-1.5 bg-slate-800 [&>div]:bg-[#9ACD32]" />
    </Card>
  );
}

function ActionCard({ to, icon: Icon, label, accent, testId }) {
  const isLime = accent === "lime";
  return (
    <Link to={to} className="group" data-testid={testId}>
      <Card
        className={`flex items-center gap-4 border-slate-800 bg-[#0A0C1F] p-5 transition-all hover:-translate-y-1 ${
          isLime ? "hover:border-[#9ACD32]" : "hover:border-slate-600"
        }`}
      >
        <div
          className={`flex h-12 w-12 items-center justify-center rounded-xl ${
            isLime ? "bg-[#9ACD32]/15 text-[#9ACD32]" : "bg-[#1A1D7A]/30 text-[#9ACD32]"
          }`}
        >
          <Icon className="h-5 w-5" />
        </div>
        <span className="flex-1 font-semibold text-white">{label}</span>
        <ArrowRight className="h-4 w-4 text-slate-500 transition-transform group-hover:translate-x-1" />
      </Card>
    </Link>
  );
}
